import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../hooks/useAuth';
import { BarChart, Calendar, Users, Phone, ShoppingCart, ChevronDown, ChevronUp, PieChart, Globe, User, Check, RefreshCw, XCircle } from 'lucide-react';

const Statistics = () => {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  
  // Stati per il filtro delle date - usiamo stringhe per gli input nativi
  const [startDate, setStartDate] = useState(formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30)))); // Default: ultimi 30 giorni
  const [endDate, setEndDate] = useState(formatDateForInput(new Date()));
  
  // Stati per i dati delle statistiche
  const [callStats, setCallStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  
  // Stati per il filtro utente (solo per admin)
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showGlobalStats, setShowGlobalStats] = useState(true); // Default a true per mostrare le statistiche globali
  
  // Stati per i dati aggregati
  const [dailyStats, setDailyStats] = useState([]);
  
  // Stati per la paginazione della tabella delle chiamate
  const [currentPage, setCurrentPage] = useState(1);
  const [callsPerPage] = useState(10);
  
  
  // Funzione per formattare la data per l'input di tipo date (YYYY-MM-DD)
  function formatDateForInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // Funzione per convertire la stringa dell'input in un oggetto Date
  function parseInputDate(dateString) {
    return new Date(dateString);
  }
  
  // Funzione per resettare tutti i filtri
  const resetFilters = () => {
    // Reimposta le date agli ultimi 30 giorni
    setStartDate(formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30))));
    setEndDate(formatDateForInput(new Date()));
    
    // Reimposta il filtro utente
    if (isAdmin) {
      setShowGlobalStats(true);
      setSelectedUserId(null);
    } else {
      setSelectedUserId(user?.id);
      setShowGlobalStats(false);
    }
    
    // Chiude tutti i dropdown
    setShowUserDropdown(false);
    
    // Reimposta la paginazione
    setCurrentPage(1);
  };
  
  useEffect(() => {
    // Se l'utente è admin, carica la lista degli utenti
    if (isAdmin) {
      fetchUsers();
      // Per gli admin, mostra le statistiche globali di default
      setShowGlobalStats(true);
      setSelectedUserId(null);
    } else {
      // Per utenti normali, imposta l'ID utente corrente e disabilita le statistiche globali
      setSelectedUserId(user?.id);
      setShowGlobalStats(false);
    }
  }, [isAdmin, user]);
  
  useEffect(() => {
    // Crea un AbortController per questa chiamata
    const abortController = new AbortController();
    
    // Debounce per le chiamate API - attende 500ms dopo l'ultimo cambio
    const timeoutId = setTimeout(() => {
      // Carica le statistiche quando cambiano le date, l'utente selezionato, l'opzione globale o la campagna selezionata
      if ((showGlobalStats && isAdmin) || selectedUserId) {
        fetchCallStats(abortController.signal);
      }
    }, 500);
    
    // Cleanup: annulla il timeout e la chiamata API se le dipendenze cambiano prima dei 500ms
    return () => {
      clearTimeout(timeoutId);
      abortController.abort(); // Annulla la chiamata API in corso
    };
  }, [startDate, endDate, selectedUserId, showGlobalStats, isAdmin]);
  
  // Aggiungiamo un effetto per assicurarci che gli utenti non admin possano vedere solo le proprie statistiche
  useEffect(() => {
    // Se l'utente non è admin e sta cercando di visualizzare le statistiche globali o di un altro utente
    if (!isAdmin) {
      if (showGlobalStats || (selectedUserId && user?.id && selectedUserId !== user.id)) {
        // Reimposta l'ID utente selezionato a quello dell'utente corrente e disabilita le statistiche globali
        setSelectedUserId(user?.id);
        setShowGlobalStats(false);
      }
    }
  }, [isAdmin, selectedUserId, user, showGlobalStats]);
  
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };
  
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Benutzer');
      }
      
      const data = await response.json();
      
      // Filtra gli admin e raggruppa per reparto
      const nonAdminUsers = data.filter(user => user.role !== 'admin');
      setUsers(nonAdminUsers);
      
      // Seleziona il primo utente come default per gli admin
      if (nonAdminUsers.length > 0) {
        setSelectedUserId(nonAdminUsers[0].id);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Fehler beim Laden der Benutzer');
    }
  };
  
  
  const fetchCallStats = async (signal) => {
    setLoading(true);
    setError(null);
    
    try {
      // Le date sono già nel formato corretto YYYY-MM-DD
      const formattedStartDate = startDate;
      const formattedEndDate = endDate;
      
      // Costruisci l'URL base con i parametri di data
      let url;
      if (showGlobalStats && isAdmin) {
        // Endpoint per le statistiche globali - solo per admin
        url = `${API_BASE_URL}/global-call-stats?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
      } else if (selectedUserId) {
        // Endpoint per le statistiche di un singolo utente
        url = `${API_BASE_URL}/users/${selectedUserId}/call-stats?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
      } else {
        // Se non c'è un utente selezionato e non siamo in modalità globale, non fare nulla
        setLoading(false);
        return;
      }
      
      
      console.log(`Chiamata API: ${url}`);
      
      const response = await fetch(url, {
        headers: getAuthHeader(),
        signal // Passa l'AbortSignal per permettere la cancellazione
      });
      
      if (!response.ok) {
        throw new Error(`Fehler beim Laden der Anrufstatistiken: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Call stats data:', data);
      
      
      setCallStats(data);
      
      // Elabora i dati per la visualizzazione giornaliera
      processDataForCharts(data);
    } catch (error) {
      // Ignora errori di cancellazione (abort)
      if (error.name === 'AbortError') {
        console.log('Chiamata API annullata');
        return;
      }
      console.error('Error fetching call statistics:', error);
      setError(`Fehler beim Laden der Anrufstatistiken: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const processDataForCharts = (data) => {
    if (!data || !data.calls) return;
    
    // Raggruppa le chiamate per data
    const dailyData = {};
    
    data.calls.forEach(call => {
      // Estrai solo la parte della data (YYYY-MM-DD) dalla data/ora completa
      const callDate = new Date(call.call_date).toISOString().split('T')[0];
      
      if (!dailyData[callDate]) {
        dailyData[callDate] = {
          date: callDate,
          total: 0,
          closed: 0
        };
      }
      
      dailyData[callDate].total += 1;
      
      // Controlla se lo stato è "closed" (potrebbe essere "closed" o "CustomerStatus.closed")
      if (call.new_status === 'closed' || 
          call.new_status === 'CustomerStatus.closed' || 
          (typeof call.new_status === 'object' && call.new_status?.name === 'closed')) {
        dailyData[callDate].closed += 1;
      }
    });
    
    // Converti in array e ordina per data
    const sortedDailyStats = Object.values(dailyData).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    setDailyStats(sortedDailyStats);
  };
  
  const formatDate = (dateString, needsAdjustment = false) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    // Aggiungiamo un'ora per compensare la differenza tra UTC e ora locale
    // solo se il timestamp necessita di correzione
    const date = new Date(dateString);
    const adjustedDate = needsAdjustment 
      ? new Date(date.getTime() + 60 * 60 * 1000)
      : date;
    return adjustedDate.toLocaleDateString('de-DE', options);
  };
  
  const getSelectedUserName = () => {
    if (showGlobalStats) return 'Alle Benutzer';
    if (!isAdmin) return user?.username;
    
    const selectedUser = users.find(u => u.id === selectedUserId);
    return selectedUser ? selectedUser.username : 'Kein Benutzer ausgewählt';
  };
  
  
  // Funzione per raggruppare gli utenti per reparto
  const groupUsersByDepartment = () => {
    const groups = {};
    
    users.forEach(user => {
      const dept = user.department || 'Keine Abteilung';
      if (!groups[dept]) {
        groups[dept] = [];
      }
      groups[dept].push(user);
    });
    
    // Ordina i gruppi: "Keine Abteilung" alla fine
    const sortedGroups = Object.keys(groups).sort((a, b) => {
      if (a === 'Keine Abteilung') return 1;
      if (b === 'Keine Abteilung') return -1;
      return a.localeCompare(b);
    });
    
    return sortedGroups.map(dept => ({
      department: dept,
      users: groups[dept].sort((a, b) => a.username.localeCompare(b.username))
    }));
  };
  
  
  // Funzione per calcolare il numero di chiamate vendute dai dati ricevuti
  const calculateSoldCalls = (data) => {
    if (!data || !data.calls || !data.calls.length) return 0;
    
    return data.calls.filter(call => 
      call.new_status === 'sold' || 
      call.new_status === 'CustomerStatus.sold' || 
      (typeof call.new_status === 'object' && call.new_status?.name === 'sold')
    ).length;
  };
  
  // Funzione per calcolare il numero di chiamate chiuse dai dati ricevuti
  const calculateClosedCalls = (data) => {
    if (!data || !data.calls || !data.calls.length) return 0;
    
    return data.calls.filter(call => 
      call.new_status === 'closed' || 
      call.new_status === 'CustomerStatus.closed' || 
      (typeof call.new_status === 'object' && call.new_status?.name === 'closed')
    ).length;
  };
  
  // Calcola gli indici delle chiamate da visualizzare nella pagina corrente
  const indexOfLastCall = currentPage * callsPerPage;
  const indexOfFirstCall = indexOfLastCall - callsPerPage;
  const currentCalls = callStats?.calls ? callStats.calls.slice(indexOfFirstCall, indexOfLastCall) : [];
  
  // Calcola il numero totale di pagine
  const totalPages = callStats?.calls ? Math.ceil(callStats.calls.length / callsPerPage) : 0;
  
  // Funzione per cambiare pagina
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Funzione per andare alla pagina successiva
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Funzione per andare alla pagina precedente
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  return (
    <div>
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold flex items-center">
          <PieChart className="mr-2 h-5 w-5 text-indigo-500" />
          Statistiken
        </h2>
      </div>
      <div className="p-6">
      <div className="bg-white shadow-sm rounded-lg p-4 sm:p-6">
        {/* Sezione dei filtri */}
        <div className="mb-4">
          {/* Filtri in una riga */}
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between mb-3">
            {/* Filtri date e utente */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              {/* Filtro date */}
              <div className="flex flex-wrap items-center gap-2">
                <Calendar className="h-5 w-5 text-gray-500 flex-shrink-0" />
                <div className="flex items-center">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full sm:w-auto border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                  <span className="mx-2">-</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full sm:w-auto border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
              </div>
              
              {/* Filtro Utente - solo per admin */}
              {isAdmin && (
                <div className="relative">
                  <button
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-48"
                  >
                    <User className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                    <span className="mr-2 truncate">{getSelectedUserName()}</span>
                    {showUserDropdown ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
                  </button>
                  
                  {showUserDropdown && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm max-h-60">
                      <div
                        className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                          showGlobalStats ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                        }`}
                        onClick={() => {
                          setShowGlobalStats(true);
                          setSelectedUserId(null);
                          setShowUserDropdown(false);
                        }}
                      >
                        <div className="flex items-center">
                          <Globe className="h-4 w-4 mr-2" />
                          <span className="block truncate">Alle Benutzer</span>
                        </div>
                        {showGlobalStats && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                            <Check className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                      
                      {groupUsersByDepartment().map((group, groupIndex) => (
                        <div key={groupIndex}>
                          {group.department !== 'Keine Abteilung' && (
                            <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                              {group.department}
                            </div>
                          )}
                          {group.users.map((user) => (
                            <div
                              key={user.id}
                              className={`cursor-pointer select-none relative py-2 pl-6 pr-9 ${
                                !showGlobalStats && selectedUserId === user.id ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                              }`}
                              onClick={() => {
                                setShowGlobalStats(false);
                                setSelectedUserId(user.id);
                                setShowUserDropdown(false);
                              }}
                            >
                              <div className="flex items-center">
                                <User className="h-4 w-4 mr-2" />
                                <span className="block">{user.username}</span>
                              </div>
                              {!showGlobalStats && selectedUserId === user.id && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                  <Check className="h-4 w-4" />
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Pulsanti di controllo */}
            <div className="flex gap-2">
              <button
                onClick={resetFilters}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                title="Filter zurücksetzen"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Banner filtro attivo */}
        {(startDate || endDate || (!showGlobalStats && selectedUserId)) && (
          <div className="border-t border-gray-200 pt-3 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Aktiver Filter:</span>
              {startDate && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  von {startDate}
                </span>
              )}
              {endDate && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  bis {endDate}
                </span>
              )}
              {isAdmin && !showGlobalStats && selectedUserId && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Benutzer: {getSelectedUserName()}
                </span>
              )}
            </div>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : callStats ? (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="bg-indigo-50 p-3 sm:p-4 rounded-lg">
                <div className="flex items-center">
                  <Phone className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-indigo-600">Gesamtanrufe</p>
                    <p className="text-xl sm:text-2xl font-bold text-indigo-900">{callStats.total_calls || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 p-3 sm:p-4 rounded-lg">
                <div className="flex items-center">
                  <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-600 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-600">Geschlossen</p>
                    <p className="text-xl sm:text-2xl font-bold text-red-900">
                      {callStats.closed_calls || calculateClosedCalls(callStats) || 0}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 sm:p-4 rounded-lg sm:col-span-2 lg:col-span-1">
                <div className="flex items-center">
                  <BarChart className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-600">Conversion Rate</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-900">
                      {callStats.total_calls > 0
                        ? (((callStats.closed_calls || calculateClosedCalls(callStats)) / callStats.total_calls) * 100).toFixed(1)
                        : 0}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3 sm:mb-4">Tägliche Anrufstatistik</h3>
              
              {dailyStats.length > 0 ? (
                <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
                  <div className="min-w-full" style={{ height: '250px', minWidth: `${dailyStats.length * 50}px` }}>
                    <div className="flex h-full">
                      {dailyStats.map((day, index) => {
                        const totalHeight = 180; // Altezza massima in pixel
                        const maxCalls = Math.max(...dailyStats.map(d => d.total), 5); // Almeno 5 per la scala
                        const totalBarHeight = (day.total / maxCalls) * totalHeight;
                        const closedBarHeight = (day.closed / maxCalls) * totalHeight;
                        
                        return (
                          <div key={index} className="flex flex-col items-center mx-1 sm:mx-2" style={{ minWidth: '40px', maxWidth: '60px', flex: '1 0 auto' }}>
                            <div className="h-44 sm:h-48 flex items-end justify-center w-full">
                              <div className="relative w-8 sm:w-12 flex flex-col items-center justify-end h-full">
                                {/* Barra per le chiamate totali */}
                                <div
                                  className="w-full bg-indigo-200 rounded-t"
                                  style={{ height: `${totalBarHeight}px` }}
                                ></div>
                                
                                {/* Barra per le chiamate chiuse */}
                                <div
                                  className="w-full bg-red-500 rounded-t absolute bottom-0"
                                  style={{ height: `${closedBarHeight}px` }}
                                ></div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 sm:mt-2 truncate w-full text-center">{formatDate(day.date, true)}</div>
                            <div className="text-xs font-medium">
                              <span className="text-indigo-600">{day.total}</span>
                              <span className="mx-1">/</span>
                              <span className="text-red-600">{day.closed}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="flex justify-center mt-3 sm:mt-4">
                    <div className="flex items-center mr-4">
                      <div className="w-3 h-3 bg-indigo-200 rounded mr-1"></div>
                      <span className="text-xs text-gray-600">Gesamtanrufe</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
                      <span className="text-xs text-gray-600">Geschlossen</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center items-center h-48 sm:h-64 text-gray-500">
                  Keine Daten für den ausgewählten Zeitraum verfügbar
                </div>
              )}
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3 sm:mb-4">Detaillierte Anrufstatistik</h3>
              
              {callStats.calls && callStats.calls.length > 0 ? (
                <div>
                  {/* Tabella per desktop (nascosta su mobile) */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Datum
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Kunde
                          </th>
                          {showGlobalStats && (
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Benutzer
                            </th>
                          )}
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Feedback
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Notizen
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentCalls.map((call, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(call.call_date).toLocaleString('de-DE')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {call.customer_name}
                            </td>
                            {showGlobalStats && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {call.username}
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                call.new_status === 'sold' || 
                                call.new_status === 'CustomerStatus.sold' || 
                                (typeof call.new_status === 'object' && call.new_status?.name === 'sold')
                                  ? 'bg-green-100 text-green-800' :
                                call.new_status === 'closed' || 
                                call.new_status === 'CustomerStatus.closed' || 
                                (typeof call.new_status === 'object' && call.new_status?.name === 'closed')
                                  ? 'bg-red-100 text-red-800' :
                                call.new_status === 'not_available' || 
                                call.new_status === 'CustomerStatus.not_available' || 
                                (typeof call.new_status === 'object' && call.new_status?.name === 'not_available')
                                  ? 'bg-yellow-100 text-yellow-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {call.new_status === 'sold' || 
                                 call.new_status === 'CustomerStatus.sold' || 
                                 (typeof call.new_status === 'object' && call.new_status?.name === 'sold')
                                  ? 'Verkauft' :
                                 call.new_status === 'closed' || 
                                 call.new_status === 'CustomerStatus.closed' || 
                                 (typeof call.new_status === 'object' && call.new_status?.name === 'closed')
                                  ? 'Geschlossen' :
                                 call.new_status === 'not_available' || 
                                 call.new_status === 'CustomerStatus.not_available' || 
                                 (typeof call.new_status === 'object' && call.new_status?.name === 'not_available')
                                  ? 'Nicht erreicht' :
                                 'Offen'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                call.new_feedback === 'positive' || 
                                call.new_feedback === 'CustomerFeedback.positive' || 
                                (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'positive')
                                  ? 'bg-green-100 text-green-800' :
                                call.new_feedback === 'negative' || 
                                call.new_feedback === 'CustomerFeedback.negative' || 
                                (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'negative')
                                  ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {call.new_feedback === 'positive' || 
                                 call.new_feedback === 'CustomerFeedback.positive' || 
                                 (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'positive')
                                  ? 'Positiv' :
                                 call.new_feedback === 'negative' || 
                                 call.new_feedback === 'CustomerFeedback.negative' || 
                                 (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'negative')
                                  ? 'Negativ' :
                                 'Neutral'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {call.notes || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Card layout per mobile (nascosto su desktop) */}
                  <div className="md:hidden space-y-3">
                    {currentCalls.map((call, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div className="text-sm font-medium text-gray-900 truncate flex-1">{call.customer_name}</div>
                          <div className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                            {new Date(call.call_date).toLocaleString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                        
                        {showGlobalStats && call.username && (
                          <div className="flex items-center mb-2">
                            <User className="h-3 w-3 text-gray-400 mr-1" />
                            <span className="text-xs text-gray-700">{call.username}</span>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            call.new_status === 'sold' || 
                            call.new_status === 'CustomerStatus.sold' || 
                            (typeof call.new_status === 'object' && call.new_status?.name === 'sold')
                              ? 'bg-green-100 text-green-800' :
                            call.new_status === 'closed' || 
                            call.new_status === 'CustomerStatus.closed' || 
                            (typeof call.new_status === 'object' && call.new_status?.name === 'closed')
                              ? 'bg-red-100 text-red-800' :
                            call.new_status === 'not_available' || 
                            call.new_status === 'CustomerStatus.not_available' || 
                            (typeof call.new_status === 'object' && call.new_status?.name === 'not_available')
                              ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {call.new_status === 'sold' || 
                             call.new_status === 'CustomerStatus.sold' || 
                             (typeof call.new_status === 'object' && call.new_status?.name === 'sold')
                              ? 'Verkauft' :
                             call.new_status === 'closed' || 
                             call.new_status === 'CustomerStatus.closed' || 
                             (typeof call.new_status === 'object' && call.new_status?.name === 'closed')
                              ? 'Geschlossen' :
                             call.new_status === 'not_available' || 
                             call.new_status === 'CustomerStatus.not_available' || 
                             (typeof call.new_status === 'object' && call.new_status?.name === 'not_available')
                              ? 'Nicht erreicht' :
                             'Offen'}
                          </span>
                          
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            call.new_feedback === 'positive' || 
                            call.new_feedback === 'CustomerFeedback.positive' || 
                            (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'positive')
                              ? 'bg-green-100 text-green-800' :
                            call.new_feedback === 'negative' || 
                            call.new_feedback === 'CustomerFeedback.negative' || 
                            (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'negative')
                              ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {call.new_feedback === 'positive' || 
                             call.new_feedback === 'CustomerFeedback.positive' || 
                             (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'positive')
                              ? 'Positiv' :
                             call.new_feedback === 'negative' || 
                             call.new_feedback === 'CustomerFeedback.negative' || 
                             (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'negative')
                              ? 'Negativ' :
                             'Neutral'}
                          </span>
                        </div>
                        
                        {call.notes && (
                          <div className="text-xs text-gray-500 border-t border-gray-100 pt-2 mt-1">
                            {call.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {callStats.calls.length > callsPerPage && (
                    <div className="mt-4 flex flex-col space-y-2">
                      <div className="text-xs text-gray-500 text-center">
                        Zeige <span className="font-medium">{indexOfFirstCall + 1}</span> bis <span className="font-medium">{Math.min(indexOfLastCall, callStats.calls.length)}</span> von <span className="font-medium">{callStats.calls.length}</span> Anrufen
                      </div>
                      <div className="flex justify-center items-center">
                        <button
                          onClick={prevPage}
                          disabled={currentPage === 1}
                          className={`px-2 py-1 rounded-md text-sm font-medium ${
                            currentPage === 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                          }`}
                        >
                          &laquo;
                        </button>
                        
                        {(() => {
                          const maxPagesToShow = 5;
                          let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                          let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
                          
                          if (endPage - startPage + 1 < maxPagesToShow) {
                            startPage = Math.max(1, endPage - maxPagesToShow + 1);
                          }
                          
                          const pages = [];
                          
                          if (startPage > 1) {
                            pages.push(
                              <button
                                key={1}
                                onClick={() => paginate(1)}
                                className="px-2 py-1 rounded-md text-sm font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                              >
                                1
                              </button>
                            );
                            if (startPage > 2) {
                              pages.push(
                                <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                                  ...
                                </span>
                              );
                            }
                          }
                          
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => paginate(i)}
                                className={`px-2 py-1 rounded-md text-sm font-medium ${
                                  currentPage === i
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                                }`}
                              >
                                {i}
                              </button>
                            );
                          }
                          
                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                                  ...
                                </span>
                              );
                            }
                            pages.push(
                              <button
                                key={totalPages}
                                onClick={() => paginate(totalPages)}
                                className="px-2 py-1 rounded-md text-sm font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                              >
                                {totalPages}
                              </button>
                            );
                          }
                          
                          return pages;
                        })()}
                        
                        <button
                          onClick={nextPage}
                          disabled={currentPage === totalPages}
                          className={`px-2 py-1 rounded-md text-sm font-medium ${
                            currentPage === totalPages
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                          }`}
                        >
                          &raquo;
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  Keine Anrufe im ausgewählten Zeitraum
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center h-64 text-gray-500">
            Wählen Sie einen Zeitraum und einen Benutzer aus, um Statistiken anzuzeigen
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Statistics; 